import { Component } from 'react'
import cn from 'classnames'

import { formatUSCurrency } from 'helpers/formatHelpers'
import { ShareMethod } from 'interfaces/share'
import { generateReferralShareable } from 'helpers/socialHelpers'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import ShareButtonContainer from 'components/features/pfm/ShareButton/ShareButtonContainer'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'

import styles from 'components/features/inviteFriendsPane/containers/InviteFriends/InviteFriends.styl'

const COPY_BUTTON_RESET_DELAY = 1000

export type InviteFriendsProps = {
  invite_link: string | null
  reward_amount: string
}

type InviteFriendsState = {
  linkCopied: boolean
}

const SHARE_METHODS: ShareMethod[] = [
  'facebook',
  'twitter',
  'email',
  'native',
]

export class InviteFriends extends Component<InviteFriendsProps, InviteFriendsState> {
  state = {
    linkCopied: false,
  }

  private inputNode: HTMLInputElement | null = null
  private copyTimeout: number

  render () {
    const { reward_amount } = this.props

    const rootClassName = cn(styles.referralInfo, {
      [styles.isStatusApp]: IS_STATUS_APP,
    })

    return (
      <section className={ rootClassName }>
        <div className={ styles.text }>
          Share your referral link and earn { formatUSCurrency(reward_amount) } for every friend who joins Status and links their credit file and an account.
        </div>
        { this.renderLinkWrapper() }
        { this.renderSocials() }
      </section>
    )
  }

  renderLinkWrapper = () => {
    const { invite_link } = this.props
    const { linkCopied } = this.state

    const linkExists = Boolean(invite_link)

    const copyButtonClassName = cn(styles.copyButton, {
      [styles.copied]: linkCopied,
    })

    const inputClassName = cn(styles.input, {
      [styles.notAvailable]: !linkExists,
    })

    return (
      <div className={ styles.linkWrapper }>
        <div className={ styles.sectionLabel }>
          Your Referral Link
        </div>
        <div className={ styles.inputButtonWrapper }>
          <div className={ styles.inputWrapper }>
            <input
              ref={ (node: HTMLInputElement) => this.inputNode = node }
              className={ inputClassName }
              type='text'
              value={ linkExists && invite_link ? invite_link : 'Not Available' }
              readOnly
            />
          </div>
          <AnalyticsClick
            className={ styles.copyButtonWrapper }
            category='site_controls-refer_friend'
            action='click'
            label='copy'
          >
            <button
              onClick={ this.onCopyButtonClick }
              className={ copyButtonClassName }
            >
              <span className={ styles.copyButtonText }>Copy</span>
              <span className={ styles.copyButtonText }>Copied</span>
            </button>
          </AnalyticsClick>
        </div>
      </div>
    )
  }

  renderSocials = () => {
    const { invite_link } = this.props
    if (!invite_link) {
      return null
    }

    const socials = SHARE_METHODS
      .map((method) => {
        const socialItemClassName = cn(styles.socialItem, {
          [styles[method]]: true,
        })

        if (
          method === 'native' && IS_STATUS_APP
          || method !== 'native' && !IS_STATUS_APP
        ) {
          return (
            <AnalyticsClick
              key={ method }
              className={ socialItemClassName }
              category='site_controls-refer_friend'
              action='click'
              label={ method }
            >
              <ShareButtonContainer { ...generateReferralShareable(method, invite_link) } />
            </AnalyticsClick>
          )
        }

        return null
      })

    return (
      <div className={ styles.socialsWrapper }>
        <div className={ styles.moreWaysLabel }>
          More Ways To Share
        </div>
        <div className={ styles.socials }>
          { socials }
        </div>
      </div>
    )
  }

  componentWillUnmount () {
    clearTimeout(this.copyTimeout)
  }

  private toggleInputSelection = (toggleValue: boolean) => {
    if (!this.inputNode) {
      return
    }

    let prevActiveElement: HTMLElement | null = null

    if (document.activeElement !== this.inputNode) {
      prevActiveElement = document.activeElement as HTMLElement
      this.inputNode.focus()
    }

    if (toggleValue) {
      this.inputNode.setSelectionRange(0, this.inputNode.value.length)
    } else {
      this.inputNode.setSelectionRange(0, 0)
    }

    if (prevActiveElement && prevActiveElement.focus) {
      prevActiveElement.focus()
    }
  }

  private onCopyButtonClick = () => {
    if (this.state.linkCopied) {
      return
    }

    this.toggleInputSelection(true)
    const copyResult = document.execCommand('copy')

    if (copyResult) {
      this.toggleInputSelection(false)

      this.setState({ linkCopied: true }, () => {
        this.copyTimeout = window.setTimeout(
          () => this.setState({ linkCopied: false }),
          COPY_BUTTON_RESET_DELAY
        )
      })
    }
  }
}
